import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { DEFAULT_INTERFACE_COLORS, normalizeInterfaceBranding, interfaceColorVariables } from './visualTheme.mjs';

/**
 * Default App Configuration
 */
const DEFAULT_CONFIG = {
  branding: {
    logoUrl: null,
    faviconUrl: null,
    appName: 'GoldBuy Portal',
    shortName: 'GoldBuy',
    tagline: 'Jewelry Business Management',
    ...DEFAULT_INTERFACE_COLORS,
  },
  theme: {
    mode: 'light',
    animationsEnabled: true,
    customCSS: '',
  },
  display: {
    headerStyle: 'solid', // 'floating' | 'solid' | 'minimal'
    cardStyle: 'solid', // 'glass' | 'solid' | 'elevated'
    showBreadcrumbs: true,
    compactMode: false,
  },
  system: {
    defaultCurrency: 'USD',
    dateFormat: 'MM/DD/YYYY',
    timezone: 'America/New_York',
    businessHours: {
      start: '09:00',
      end: '17:00',
      daysOpen: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    },
  },
};

/**
 * App Config Context
 */
const AppConfigContext = createContext({
  config: DEFAULT_CONFIG,
  loading: true,
  error: null,
  updateConfig: async () => {},
  resetConfig: async () => {},
});

/**
 * App Config Provider Component
 * Loads configuration from Firestore and provides it to the app
 */
export function AppConfigProvider({ children }) {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Apply CSS variables from config
   */
  const applyCSSVariables = (configData) => {
    const root = document.documentElement;

    // Browser-tab title follows the deployment's brand (index.html ships the
    // neutral product name only as the pre-load fallback).
    if (configData?.branding?.appName) {
      document.title = configData.branding.appName;
    }

    if (configData?.branding) {
      // Always replace stale inline values, including when a saved warm default
      // is retired. RGB channels also make Tailwind opacity modifiers work.
      Object.entries(interfaceColorVariables(configData.branding)).forEach(([name, value]) => root.style.setProperty(name, value));
    }
  };

  /**
   * Load configuration from Firestore
   */
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const configRef = doc(db, 'app_config', 'settings');
        const configSnap = await getDoc(configRef);

        if (configSnap.exists()) {
          const data = configSnap.data();
          const mergedConfig = {
            ...DEFAULT_CONFIG,
            ...data,
            branding: normalizeInterfaceBranding({ ...DEFAULT_CONFIG.branding, ...data.branding }),
            theme: { ...DEFAULT_CONFIG.theme, ...data.theme },
            display: { ...DEFAULT_CONFIG.display, ...data.display },
            system: { ...DEFAULT_CONFIG.system, ...data.system },
          };
          setConfig(mergedConfig);
          applyCSSVariables(mergedConfig);
        } else {
          // Create default config in Firestore
          await setDoc(configRef, {
            ...DEFAULT_CONFIG,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          });
          applyCSSVariables(DEFAULT_CONFIG);
        }
      } catch (error) {
        console.error('Error loading config:', error);
        setError('Failed to load app configuration');
        applyCSSVariables(DEFAULT_CONFIG);
      } finally {
        setLoading(false);
      }
    };

    loadConfig();

    // Set up real-time listener for config changes
    const configRef = doc(db, 'app_config', 'settings');
    const unsubscribe = onSnapshot(configRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        const mergedConfig = {
          ...DEFAULT_CONFIG,
          ...data,
          branding: normalizeInterfaceBranding({ ...DEFAULT_CONFIG.branding, ...data.branding }),
          theme: { ...DEFAULT_CONFIG.theme, ...data.theme },
          display: { ...DEFAULT_CONFIG.display, ...data.display },
          system: { ...DEFAULT_CONFIG.system, ...data.system },
        };
        setConfig(mergedConfig);
        applyCSSVariables(mergedConfig);
      }
    }, (error) => {
      console.error('Error listening to config changes:', error);
      setError('Failed to sync app configuration');
    });

    return () => unsubscribe();
  }, []);

  /**
   * Update configuration in Firestore
   */
  const updateConfig = async (updates) => {
    try {
      const configRef = doc(db, 'app_config', 'settings');
      await setDoc(configRef, {
        ...updates,
        updatedAt: new Date().toISOString(),
      }, { merge: true });

      // Update local state immediately
      const mergedConfig = {
        ...config,
        ...updates,
        branding: normalizeInterfaceBranding({ ...config.branding, ...updates.branding }),
        theme: { ...config.theme, ...updates.theme },
        display: { ...config.display, ...updates.display },
        system: { ...config.system, ...updates.system },
      };
      setConfig(mergedConfig);
      applyCSSVariables(mergedConfig);

      return { success: true };
    } catch (error) {
      console.error('Error updating config:', error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Reset configuration to defaults
   */
  const resetConfig = async () => {
    try {
      const configRef = doc(db, 'app_config', 'settings');
      await setDoc(configRef, {
        ...DEFAULT_CONFIG,
        updatedAt: new Date().toISOString(),
      });

      setConfig(DEFAULT_CONFIG);
      applyCSSVariables(DEFAULT_CONFIG);

      return { success: true };
    } catch (error) {
      console.error('Error resetting config:', error);
      return { success: false, error: error.message };
    }
  };

  const value = {
    config,
    loading,
    error,
    updateConfig,
    resetConfig,
  };

  return (
    <AppConfigContext.Provider value={value}>
      {children}
    </AppConfigContext.Provider>
  );
}

/**
 * Hook to use app configuration
 */
export function useAppConfig() {
  const context = useContext(AppConfigContext);
  if (!context) {
    throw new Error('useAppConfig must be used within AppConfigProvider');
  }
  return context;
}

export default AppConfigContext;
